import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import PullToRefresh from "../components/PullToRefresh";
import {
    checklistApi,
    getStaff,
    unwrapSubmissionList,
} from "../utils/Api";
import {
    formatBusinessDate,
    formatDateTime,
    labelize,
} from "../utils/format";

const STATUS_BADGE = {
    SUBMITTED: "bg-green-100 text-green-600",
    DRAFT: "bg-sky-100 text-sky-600",
    PENDING: "bg-amber-100 text-amber-600",
    MISSED: "bg-red-100 text-red-600",
};

function normalizeRow(row) {
    return {
        id: row.id ?? row.submission_id,
        checklistType: row.checklist_type,
        frequency: row.frequency,
        businessDate: row.business_date,
        status: String(row.status || "").toUpperCase(),
        submittedAt: row.submitted_at || row.updated_at || null,
        staffName:
            row.staff_name ||
            row.submitted_by_name ||
            row.submitted_by ||
            "",
        categoryName: row.category_name || "",
        yesCount: Number.isFinite(Number(row.yes_count))
            ? Number(row.yes_count)
            : null,
        noCount: Number.isFinite(Number(row.no_count))
            ? Number(row.no_count)
            : null,
        isOwn: row.is_own_submission,
    };
}

export default function History() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState("all");
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(
        async (nextPage = 1, append = false, options = {}) => {
            const staff = getStaff();

            if (filter === "mine" && !staff?.id) {
                setError("Your profile is missing a staff id, so this filter cannot run.");
                setItems([]);
                setHasMore(false);
                setLoading(false);
                return;
            }

            if (append) setLoadingMore(true);
            else if (!options.silent) setLoading(true);

            try {
                setError("");
                const json = await checklistApi.listSubmissions({
                    ...(filter === "mine" ? { staff_id: staff.id } : {}),
                    ...(nextPage > 1
                        ? { page: nextPage, per_page: 20 }
                        : {}),
                });
                const { rows, pagination } = unwrapSubmissionList(json?.data);
                const normalized = rows.map(normalizeRow).filter((row) => row.id);

                setItems((current) =>
                    append ? [...current, ...normalized] : normalized
                );
                setPage(nextPage);

                const totalPages = Number(
                    pagination?.total_pages || pagination?.last_page || 0
                );
                setHasMore(totalPages > nextPage);
            } catch (loadError) {
                if (loadError?.status === 401) return;

                setError(loadError?.message || "Unable to load submission history.");

                if (!append) setItems([]);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [filter]
    );

    useEffect(() => {
        load(1, false);
    }, [load]);

    return (
        <main className="min-h-screen bg-gradient-to-b from-[#18c2dc] via-[#08a8cc] to-[#017fa8] flex justify-center font-sans">
            <section className="relative w-full max-w-[430px] min-h-screen bg-[#F6FBFD] shadow-[0_24px_70px_rgba(14,73,97,0.26)] overflow-hidden">
                <AppHeader />

                <PullToRefresh onRefresh={() => load(1, false, { silent: true })}>
                <div className="px-5 pt-5 pb-10">
                    <button
                        type="button"
                        onClick={() => navigate("/home")}
                        className="text-[#09A5F7] text-sm font-extrabold mb-5"
                    >
                        ← Back
                    </button>

                    <h1 className="text-[24px] font-extrabold text-[#0E4961]">
                        Submission history
                    </h1>
                    {/* <div className="mt-4 flex gap-2">
                        {[
                            { id: "all", label: "All outlet" },
                            { id: "mine", label: "Mine" },
                        ].map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => setFilter(option.id)}
                                className={`h-10 px-4 rounded-full text-xs font-extrabold border ${
                                    filter === option.id
                                        ? "bg-[#09A5F7] border-[#09A5F7] text-white"
                                        : "bg-white border-[#DDF0F6] text-[#0E4961]"
                                }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div> */}

                    <div className="mt-2 space-y-3">
                        {loading ? (
                            <div className="rounded-[24px] bg-white border border-[#D7EEF6] p-5 text-center">
                                <div className="w-9 h-9 mx-auto rounded-full border-[3px] border-[#09A5F7]/20 border-t-[#09A5F7] animate-spin" />
                                <p className="mt-3 text-sm font-extrabold text-[#0E4961]">
                                    Loading history...
                                </p>
                            </div>
                        ) : error ? (
                            <div className="rounded-[24px] bg-red-50 border border-red-100 p-5 text-center">
                                <p className="text-sm font-extrabold text-red-500">
                                    {error}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => load(1, false)}
                                    className="mt-4 px-5 h-10 rounded-[14px] bg-red-500 text-white text-xs font-extrabold"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : items.length === 0 ? (
                            <div className="rounded-[24px] bg-white border border-[#D7EEF6] p-5 text-center">
                                <p className="text-sm font-extrabold text-[#0E4961]">
                                    No submissions yet
                                </p>
                            </div>
                        ) : (
                            items.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => navigate(`/history/${item.id}`)}
                                    className="w-full text-left rounded-[24px] bg-white border border-[#DDF0F6] p-4 shadow-[0_12px_30px_rgba(14,73,97,0.08)]"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="text-[11px] uppercase tracking-wide text-[#09A5F7] font-black">
                                                {labelize(item.checklistType)}
                                                {item.frequency
                                                    ? ` · ${labelize(item.frequency)}`
                                                    : ""}
                                            </p>
                                            <h2 className="text-[#0E4961] font-extrabold mt-1">
                                                {item.categoryName ||
                                                    formatBusinessDate(item.businessDate) ||
                                                    `Submission ${item.id}`}
                                            </h2>
                                        </div>
                                        {item.status && (
                                            <span
                                                className={`rounded-full px-2 py-1 text-[10px] font-black ${
                                                    STATUS_BADGE[item.status] ||
                                                    "bg-slate-100 text-slate-500"
                                                }`}
                                            >
                                                {item.status}
                                            </span>
                                        )}
                                    </div>

                                    <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-bold text-[#6E8794]">
                                        {item.businessDate && (
                                            <span className="rounded-full bg-[#F7FBFD] border border-[#DDF0F6] px-3 py-1">
                                                {formatBusinessDate(item.businessDate)}
                                            </span>
                                        )}
                                        {item.staffName && (
                                            <span className="rounded-full bg-[#F7FBFD] border border-[#DDF0F6] px-3 py-1">
                                                {item.isOwn ? "You" : item.staffName}
                                            </span>
                                        )}
                                        {item.noCount > 0 && (
                                            <span className="rounded-full bg-red-50 border border-red-100 px-3 py-1 text-red-500">
                                                {item.noCount} NO
                                            </span>
                                        )}
                                        {item.yesCount > 0 && (
                                            <span className="rounded-full bg-green-50 border border-green-100 px-3 py-1 text-green-600">
                                                {item.yesCount} YES
                                            </span>
                                        )}
                                    </div>

                                    {item.submittedAt && (
                                        <p className="mt-3 text-[11px] font-medium text-[#6E8794]">
                                            {formatDateTime(item.submittedAt)}
                                        </p>
                                    )}
                                </button>
                            ))
                        )}
                    </div>

                    {hasMore && !loading && !error && (
                        <button
                            type="button"
                            onClick={() => load(page + 1, true)}
                            disabled={loadingMore}
                            className="mt-4 w-full h-12 rounded-[16px] bg-white border border-[#DDF0F6] text-[#09A5F7] text-sm font-extrabold disabled:opacity-60"
                        >
                            {loadingMore ? "Loading..." : "Load more"}
                        </button>
                    )}
                </div>
                </PullToRefresh>
            </section>
        </main>
    );
}
